/* Keep Shopify's sort order within the available and sold-out product groups.
 * A small ID/availability index covers the whole filtered collection. Only the
 * native pages containing this display page's cards need their HTML fetched.
 */
(() => {
  const abortIfNeeded = (signal) => {
    if (signal?.aborted) throw signal.reason || new DOMException('Aborted', 'AbortError');
  };

  function planPage(products, page, pageSize) {
    if (!Number.isInteger(page) || page < 1 || !Number.isInteger(pageSize) || pageSize < 1) {
      throw new Error('Invalid collection pagination');
    }
    const ids = new Set();
    const available = [];
    const soldOut = [];
    products.forEach(([id, inStock], index) => {
      if (typeof id !== 'string' || !id || ids.has(id) || typeof inStock !== 'boolean') {
        throw new Error('Invalid or duplicate product in availability index');
      }
      ids.add(id);
      (inStock ? available : soldOut).push({ id, available: inStock, sourcePage: Math.floor(index / pageSize) + 1 });
    });
    return available.concat(soldOut).slice((page - 1) * pageSize, page * pageSize);
  }

  function requestUrl(inputUrl, sectionId, page) {
    const url = new URL(inputUrl);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));
    return url;
  }

  function indexCoversPage(products, page, pageSize, total) {
    return products.length === total || products.filter((product) => product[1]).length >= page * pageSize;
  }

  async function mapLimit(items, limit, fn, signal) {
    const results = new Array(items.length);
    let next = 0;
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length) {
        abortIfNeeded(signal);
        const index = next++;
        results[index] = await fn(items[index], index);
        abortIfNeeded(signal);
      }
    }));
    abortIfNeeded(signal);
    return results;
  }

  // Keep the page-planning contract testable without a DOM or Shopify account.
  if (typeof module === 'object' && module.exports) {
    module.exports = { planPage, requestUrl, mapLimit, indexCoversPage };
    return;
  }
  if (window.CollectionAvailability) return;

  const metadataSelector = '[data-collection-availability]';
  const indexSelector = '[data-collection-availability-index]';
  let indexCache;
  let initialController;
  let initialPromise = Promise.resolve();

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const readJSON = (doc, selector) => {
    const element = doc.querySelector(selector);
    if (!element) throw new Error('Missing collection section response');
    return JSON.parse(element.textContent);
  };

  function mergeProductAnalytics(doc) {
    const meta = window.ShopifyAnalytics?.meta;
    if (!Array.isArray(meta?.products)) return;
    const products = new Map(meta.products.map((product) => [String(product.id), product]));
    doc.querySelectorAll('[data-collection-product-analytics]').forEach((marker) => {
      const product = JSON.parse(marker.textContent);
      // Shopify's event listener resolves card variants from this page metadata.
      // Preserve existing fields while supplying products imported from other pages.
      const existing = products.get(String(product.id));
      products.set(String(product.id), { ...existing, ...product });
    });
    meta.products = [...products.values()];
  }

  async function fetchHTML(url, signal) {
    abortIfNeeded(signal);
    const controller = new AbortController();
    const onAbort = () => controller.abort(signal.reason);
    signal?.addEventListener('abort', onAbort, { once: true });
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Collection request failed (${response.status})`);
      return await response.text();
    }
    finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', onAbort);
    }
  }

  function indexKey(url) {
    const key = new URL(url);
    key.hash = '';
    ['page', 'section_id', 'sections'].forEach((name) => key.searchParams.delete(name));
    key.searchParams.sort();
    return key.toString();
  }

  async function loadIndex(url, metadata, signal) {
    const key = indexKey(url);
    const cacheMatches = indexCache?.key === key && indexCache.total === metadata.total && indexCache.collectionId === metadata.collectionId;
    if (cacheMatches && indexCoversPage(indexCache.products, metadata.page, metadata.pageSize, metadata.total)) {
      return indexCache.products;
    }
    if (metadata.total > 25000) throw new Error('Collection exceeds Shopify pagination limits');
    const getPage = async (page) => {
      const html = await fetchHTML(requestUrl(url, 'collection-availability-index', page), signal);
      const data = readJSON(parse(html), indexSelector);
      if (data.collectionId !== metadata.collectionId || data.total !== metadata.total || data.page !== page || data.pages !== Math.ceil(metadata.total / 250) || !Array.isArray(data.products) || data.products.length !== Math.min(250, metadata.total - (page - 1) * 250)) {
        throw new Error('Collection changed while reading availability');
      }
      return data;
    };
    const products = cacheMatches ? [...indexCache.products] : (await getPage(1)).products;
    // A prefix with enough available products proves this page's exact order.
    // Only pages reaching the sold-out group need to read the entire index.
    while (!indexCoversPage(products, metadata.page, metadata.pageSize, metadata.total)) {
      const nextPage = Math.floor(products.length / 250) + 1;
      const count = Math.min(4, Math.ceil(metadata.total / 250) - nextPage + 1);
      const batch = await mapLimit(Array.from({ length: count }, (_, index) => nextPage + index), 4, getPage, signal);
      products.push(...batch.flatMap((data) => data.products));
    }
    // Validate every loaded entry, including those outside the visible slice.
    planPage(products, 1, metadata.pageSize);
    abortIfNeeded(signal);
    indexCache = { key, total: metadata.total, collectionId: metadata.collectionId, products };
    return products;
  }

  async function prepare(url, responseText, { signal, refresh = false } = {}) {
    const doc = parse(responseText);
    const marker = doc.querySelector(metadataSelector);
    if (!marker) return responseText; // Search and other theme sections are unaffected.
    try {
      abortIfNeeded(signal);
      if (refresh) indexCache = undefined;
      const metadata = JSON.parse(marker.textContent);
      if (metadata.total <= metadata.pageSize) {
        // Single-page collections are already partitioned in Liquid, even without JS.
        marker.dataset.availabilityState = 'ready';
        mergeProductAnalytics(doc);
        return doc.body.innerHTML;
      }
      const products = await loadIndex(url, metadata, signal);
      const desired = planPage(products, metadata.page, metadata.pageSize);
      const grid = doc.querySelector('#ProductGridContainer > motion-list');
      if (!grid) throw new Error('Missing collection grid');
      const original = Array.from(grid.children).filter((item) => item.hasAttribute('data-product-id'));
      if (original.length !== desired.length) throw new Error('Collection page size changed');
      const cards = new Map(original.map((card) => [card.dataset.productId, card]));
      const sourcePages = [...new Set(desired.filter((product) => !cards.has(product.id)).map((product) => product.sourcePage))];

      const sources = await mapLimit(sourcePages, 4, async (page) => {
        const html = await fetchHTML(requestUrl(url, metadata.sectionId, page), signal);
        const source = parse(html);
        const sourceMetadata = readJSON(source, metadataSelector);
        if (sourceMetadata.page !== page || sourceMetadata.total !== metadata.total || sourceMetadata.sectionId !== metadata.sectionId || sourceMetadata.collectionId !== metadata.collectionId) {
          throw new Error('Collection changed while loading product cards');
        }
        return source.querySelectorAll('#ProductGridContainer > motion-list > [data-product-id]');
      }, signal);
      sources.forEach((source) => source.forEach((card) => cards.set(card.dataset.productId, card)));
      desired.forEach((product) => {
        const card = cards.get(product.id);
        if (!card || card.dataset.productAvailable !== String(product.available)) {
          throw new Error('Product availability changed while loading cards');
        }
      });
      abortIfNeeded(signal);

      // Keep promotional tiles in their existing slots and retain native page links,
      // counts and filter controls. Never copy those from a different source page.
      original.forEach((oldCard, index) => {
        const replacement = doc.importNode(cards.get(desired[index].id), true);
        replacement.querySelectorAll('img[fetchpriority]').forEach((image) => image.removeAttribute('fetchpriority'));
        const cover = replacement.querySelector('.product-card__media > a.media > img');
        if (cover && index < 3) {
          cover.loading = 'eager';
          if (index === 0) cover.setAttribute('fetchpriority', 'high');
        }
        oldCard.replaceWith(replacement);
      });
      marker.dataset.availabilityState = 'ready';
      mergeProductAnalytics(doc);
      return doc.body.innerHTML;
    }
    catch (error) {
      if (signal?.aborted) throw error;
      // Preserve a usable, complete Shopify page if the enhancement cannot load.
      indexCache = undefined;
      marker.dataset.availabilityState = 'fallback';
      console.warn('Available-first collection ordering could not be loaded:', error);
      return doc.body.innerHTML;
    }
  }

  async function initialize() {
    const marker = document.querySelector(metadataSelector);
    if (!marker || marker.dataset.availabilityState) return;
    const container = marker.closest('#ProductGridContainer');
    const section = marker.closest('.shopify-section');
    if (!container || !section) return;
    initialController?.abort();
    const controller = new AbortController();
    initialController = controller;
    container.setAttribute('aria-busy', 'true');
    try {
      const metadata = JSON.parse(marker.textContent);
      if (metadata.total <= metadata.pageSize) {
        marker.dataset.availabilityState = 'ready';
        return;
      }
      const products = await loadIndex(window.location.href, metadata, controller.signal);
      const grid = container.querySelector(':scope > motion-list');
      const original = Array.from(grid?.children || []).filter((card) => card.hasAttribute('data-product-id'));
      const desired = planPage(products, metadata.page, metadata.pageSize);
      if (original.length === desired.length && original.every((card, index) => card.dataset.productId === desired[index].id)) {
        marker.dataset.availabilityState = 'ready';
        return;
      }
      // Get pristine server cards: serializing or moving initialized cards can
      // duplicate mobile slides or destroy desktop hover galleries and animations.
      const nativeHTML = await fetchHTML(requestUrl(window.location.href, metadata.sectionId, metadata.page), controller.signal);
      const html = await prepare(window.location.href, nativeHTML, { signal: controller.signal });
      if (controller.signal.aborted || !marker.isConnected) return;
      const source = parse(html).querySelector('#ProductGridContainer');
      marker.dataset.availabilityState = source.querySelector(metadataSelector).dataset.availabilityState;
      if (marker.dataset.availabilityState === 'fallback') return;
      const ordered = Array.from(source.querySelectorAll('motion-list > [data-product-id]'));
      const freshMetadata = readJSON(source, metadataSelector);
      if (ordered.length !== original.length || ['collectionId', 'total', 'page', 'pageSize'].some((key) => freshMetadata[key] !== metadata[key])) {
        throw new Error('Collection changed while initializing product cards');
      }
      // Keep promotional slots and pagination intact while replacing product cards.
      original.forEach((card, index) => {
        card.replaceWith(document.importNode(ordered[index], true));
      });
      grid.reload?.();
      document.dispatchEvent(new CustomEvent('collection:reloaded'));
    }
    catch (error) {
      if (!controller.signal.aborted) {
        indexCache = undefined;
        marker.dataset.availabilityState = 'fallback';
        console.warn('Collection ordering initialization failed:', error);
      }
    }
    finally {
      if (initialController === controller) container.removeAttribute('aria-busy');
    }
  }

  async function prepareNext(url, responseText, options) {
    // An appended page must use the same ordering as the cards already shown.
    // On an enhancement failure keep the button retryable instead of mixing
    // native and reordered pages, which could duplicate or omit products.
    if (document.querySelector(metadataSelector)?.dataset.availabilityState === 'fallback') {
      mergeProductAnalytics(parse(responseText));
      return responseText;
    }
    const html = await prepare(url, responseText, options);
    if (parse(html).querySelector(metadataSelector)?.dataset.availabilityState === 'fallback') {
      throw new Error('Collection ordering could not be loaded. Please retry loading more products.');
    }
    return html;
  }

  window.CollectionAvailability = {
    prepare,
    prepareNext,
    invalidate: () => { indexCache = undefined; },
    cancelInitial: () => { initialController?.abort(); },
    ready: () => initialPromise
  };
  const start = () => { initialPromise = initialize(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
